import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { archiveFiche, deleteFiche, listArchived, listFiches, openFiche, restoreFiche, saveFicheOrder } from "./api";
import { AccessScope } from "./access";
import { createFicheHome, createInterneHome, settleCrmHomes } from "./crmHome";
import { InterneDesk } from "./InterneDesk";
import { BureauView } from "./Bureau";
import { Cockpit } from "./Cockpit";
import { CoffreUnlockHost } from "./CoffreUnlock";
import { FicheDesk } from "./FicheDesk";
import { FicheHead } from "./FicheHead";
import { FicheMark } from "./FicheMark";
import { Modal } from "./Modal";
import { ThemeMenu } from "./theme/ThemeMenu";
import { Phone } from "./phone/Phone";
import { usePhone } from "./phone/usePhone";
import { SuiviScope } from "./Suivi";
import { loadMarks, type RelationStatus } from "./suivi";
import { Appear, FadeSwap } from "./fade";
import { ficheNeedle } from "./ficheIdentity";
import { aimFind, clearFind, Finder, type FindHit } from "./find";
import { GuideButton } from "./guide";
import { ReceivedStrip } from "./outside";
import { FicheShareHint } from "./share";
import { Welcome } from "./Welcome";
import { MonthBoard } from "./MonthBoard";
import { WeekBoard } from "./WeekBoard";
import { watchKanbanLink } from "./kanbanLink";
import { dueTodayCounts, loadWeekTasks, watchWeek } from "./week";
import { listProperties } from "./tasks";
import { warmFicheLogos } from "./FicheMark";
import type { CrmFiche, CrmSession } from "./types";

type Ready = Extract<CrmSession, { ok: true }>;

export function App({ session }: { session: Ready }) {
  const [fiches, setFiches] = useState<CrmFiche[]>([]);
  const [archived, setArchived] = useState<CrmFiche[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [seek, setSeek] = useState(false);
  const [focusTask, setFocusTask] = useState("");
  const dropFocus = useCallback(() => setFocusTask(""), []);
  const [kind, setKind] = useState<"" | RelationStatus>("");
  const [marks, setMarks] = useState<Record<number, RelationStatus>>({});
  const [bureau, setBureau] = useState(false);
  const [space, setSpace] = useState<"client" | "interne">(() => (localStorage.getItem("crm-space") === "interne" ? "interne" : "client"));
  const [month, setMonth] = useState(false);
  const [fiche, setFiche] = useState<CrmFiche | null>(null);
  const [ready, setReady] = useState(false);
  const [naming, setNaming] = useState(false);
  const [editName, setEditName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<"suivi" | "apercu" | "fiche">("fiche");
  const [error, setError] = useState("");
  const [pending, setPending] = useState<CrmFiche | null>(null);
  const [erasing, setErasing] = useState<CrmFiche | null>(null);
  const [dropping, setDropping] = useState(false);
  const [slim, setSlim] = useState(() => localStorage.getItem("crm-sidebar-slim") === "1");
  const [due, setDue] = useState<Record<number, number>>({});
  const [lift, setLift] = useState<number | null>(null);
  const [slot, setSlot] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const slotRef = useRef<number | null>(null);
  const phone = usePhone();

  useEffect(() => {
    let alive = true;
    void Promise.all([listFiches(), listArchived()])
      .then(async ([active, stored]) => {
        if (!alive) return;
        setFiches(active);
        setArchived(stored);
        setReady(true);
        void warmFicheLogos(active.map((row) => row.logo_path), 4000);
        try {
          const settled = await settleCrmHomes([...active, ...stored]);
          if (!alive) return;
          const byId = new Map(settled.fiches.map((row) => [row.id, row]));
          setFiches(active.map((row) => byId.get(row.id) || row));
          setArchived(stored.map((row) => byId.get(row.id) || row));
          setFiche((current) => (current ? byId.get(current.id) || current : current));
          if (settled.error) setError(settled.error);
        } catch (reason: unknown) {
          if (alive) setError(reason instanceof Error ? reason.message : "CRM clients n’a pas pu être préparé.");
        }
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Fiches indisponibles");
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    // Précharge les tâches pendant le splash / fiches pour un affichage immédiat de la semaine.
    void loadWeekTasks().catch(() => undefined);
    void listProperties().catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("crm-sidebar-slim", slim ? "1" : "0");
  }, [slim]);

  useEffect(() => {
    localStorage.setItem("crm-space", space);
  }, [space]);

  useEffect(() => {
    let alive = true;
    let timer = 0;
    const pull = (wait = 0) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void loadWeekTasks()
          .then((tasks) => {
            if (!alive) return;
            const counts: Record<number, number> = {};
            dueTodayCounts(tasks, fiches).forEach((count, id) => { counts[id] = count; });
            setDue(counts);
          })
          .catch(() => undefined);
      }, wait);
    };
    pull();
    const stop = watchWeek(() => pull(250));
    const stopLink = watchKanbanLink(() => pull(0));
    return () => {
      alive = false;
      window.clearTimeout(timer);
      stop();
      stopLink();
    };
  }, [fiches]);

  useEffect(() => {
    void loadMarks().then((rows) => {
      const next: Record<number, RelationStatus> = {};
      rows.forEach((row) => { next[row.id] = row.relation_status; });
      setMarks(next);
    });
  }, []);

  const paint = useCallback((id: number, status: RelationStatus) => {
    setMarks((prev) => ({ ...prev, [id]: status }));
  }, []);

  function remember(next: CrmFiche) {
    setFiche(next);
    setFiches((rows) => {
      const index = rows.findIndex((row) => row.id === next.id);
      const copy = index < 0 ? [...rows, next] : rows.map((row) => (row.id === next.id ? next : row));
      if (index < 0) void saveFicheOrder(copy.map((row) => row.id)).catch(() => undefined);
      return copy;
    });
  }

  function retire(row: CrmFiche, close = false) {
    setFiches((rows) => rows.filter((item) => item.id !== row.id));
    setArchived((rows) => (rows.some((item) => item.id === row.id) ? rows.map((item) => (item.id === row.id ? row : item)) : [...rows, row]));
    if (close) setFiche(null);
  }

  function startNew() {
    setDraftName("");
    setNaming(true);
  }

  async function createNamed() {
    const name = draftName.trim();
    if (!name || creating) return;
    setCreating(true);
    setError("");
    try {
      const home = space === "interne" ? await createInterneHome(name) : await createFicheHome(name);
      const next = await openFiche(home, 0, name, space === "interne" ? "interne" : "client");
      remember(next);
      setBureau(false);
      setTab("fiche");
      setNaming(false);
      setDraftName("");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Fiche non créée");
    } finally {
      setCreating(false);
    }
  }

  async function drop(id: number) {
    await archiveFiche(id);
    const row = fiches.find((item) => item.id === id);
    const nextActive = fiches.filter((item) => item.id !== id);
    const nextArchived = row ? [...archived, row] : archived;
    setFiches(nextActive);
    if (row) setArchived(nextArchived);
    if (nextActive.length > 0) void saveFicheOrder(nextActive.map((item) => item.id)).catch(() => undefined);
    if (nextArchived.length > 0) void saveFicheOrder(nextArchived.map((item) => item.id)).catch(() => undefined);
    setFiche(null);
    setTab("fiche");
    setBureau(false);
  }

  async function erase(id: number) {
    await deleteFiche(id);
    setArchived((rows) => rows.filter((item) => item.id !== id));
    setFiche((current) => (current?.id === id ? null : current));
    setShowArchived(true);
  }

  async function restore(id: number) {
    await restoreFiche(id);
    const row = archived.find((item) => item.id === id);
    const nextArchived = archived.filter((item) => item.id !== id);
    const nextActive = row ? [...fiches, row] : fiches;
    setArchived(nextArchived);
    if (row) setFiches(nextActive);
    if (nextActive.length > 0) void saveFicheOrder(nextActive.map((item) => item.id)).catch(() => undefined);
    if (nextArchived.length > 0) void saveFicheOrder(nextArchived.map((item) => item.id)).catch(() => undefined);
    setFiche(null);
    setShowArchived(false);
  }

  const clientFiches = fiches.filter((row) => row.scope !== "interne");
  const interneFiches = fiches.filter((row) => row.scope === "interne");
  const clientArchived = archived.filter((row) => row.scope !== "interne");
  const interneArchived = archived.filter((row) => row.scope === "interne");
  const pool = showArchived ? (space === "interne" ? interneArchived : clientArchived) : (space === "interne" ? interneFiches : clientFiches);
  const phonePool = showArchived ? clientArchived : clientFiches;
  const needle = query.trim().toLowerCase();
  const searched = needle === "" ? pool : pool.filter((row) => ficheNeedle(row).includes(needle));
  const visible = space === "interne" || kind === "" ? searched : searched.filter((row) => (marks[row.id] || "prospect") === kind);
  const phoneSearched = needle === "" ? phonePool : phonePool.filter((row) => ficheNeedle(row).includes(needle));
  const phoneVisible = kind === "" ? phoneSearched : phoneSearched.filter((row) => (marks[row.id] || "prospect") === kind);
  const saved = fiche !== null && (fiches.some((row) => row.id === fiche.id) || archived.some((row) => row.id === fiche.id));
  const viewKey = !ready ? ""
    : fiche && saved ? `fiche:${fiche.id}`
    : fiche ? `nouveau:${fiche.id}`
    : bureau ? "bureau"
    : showArchived ? "archives"
    : space === "client" && clientFiches.length === 0 ? "welcome"
    : month ? "mois"
    : "semaine";

  function openFromBoard(id: number) {
    const row = fiches.find((item) => item.id === id);
    if (!row) return;
    setSpace(row.scope === "interne" ? "interne" : "client");
    setFiche(row);
    setTab("apercu");
    setBureau(false);
    setMonth(false);
    setError("");
  }

  function enterSpace(next: "client" | "interne") {
    if (next === space) return;
    setSpace(next);
    setFiche(null);
    setBureau(false);
    setMonth(false);
    setShowArchived(false);
    setKind("");
    setError("");
  }

  function openHit(hit: FindHit) {
    setQuery("");
    setSeek(false);
    setError("");
    if (hit.kind === "Tâche") {
      clearFind();
      setFiche(null);
      setBureau(false);
      setMonth(false);
      setShowArchived(false);
      setFocusTask(hit.taskId);
      return;
    }
    const row = [...fiches, ...archived].find((item) => item.id === hit.ficheId);
    if (!row) return;
    setSpace(row.scope === "interne" ? "interne" : "client");
    if (hit.kind === "Fiche") clearFind();
    else aimFind(hit);
    setFiche(row);
    setTab("apercu");
    setBureau(false);
    setMonth(false);
    setShowArchived(archived.some((item) => item.id === row.id));
  }

  function grabFiche(id: number, event: ReactPointerEvent<HTMLElement>) {
    if (event.button !== 0 || (event.target as HTMLElement).closest("[data-no-drag]")) return;
    const startY = event.clientY;
    let moved = false;
    const blockSelect = (native: Event) => native.preventDefault();
    const move = (pointer: PointerEvent) => {
      if (!moved && Math.abs(pointer.clientY - startY) < 5) return;
      if (!moved) {
        moved = true;
        document.body.classList.add("crm-grabbing");
        window.getSelection()?.removeAllRanges();
        setLift(id);
      }
      pointer.preventDefault();
      const next = ficheSlot(listRef.current, pointer.clientY);
      slotRef.current = next;
      setSlot(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.removeEventListener("selectstart", blockSelect);
      document.body.classList.remove("crm-grabbing");
      const chosen = slotRef.current;
      slotRef.current = null;
      setLift(null);
      setSlot(null);
      if (!moved || chosen === null) return;
      const stopClick = (native: Event) => {
        native.preventDefault();
        native.stopPropagation();
      };
      window.addEventListener("click", stopClick, true);
      window.setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
      placeFiche(id, chosen);
    };
    document.addEventListener("selectstart", blockSelect);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function placeFiche(id: number, index: number) {
    const pool = showArchived ? archived : fiches;
    const shown = visible;
    const from = shown.findIndex((row) => row.id === id);
    if (from < 0) return;
    let to = index > from ? index - 1 : index;
    if (to < 0) to = 0;
    if (to >= shown.length) to = shown.length - 1;
    if (to === from) return;
    const nextShown = shown.filter((row) => row.id !== id);
    nextShown.splice(to, 0, shown[from]);
    const queue = [...nextShown];
    const nextPool = pool.map((row) => (shown.some((item) => item.id === row.id) ? queue.shift() || row : row));
    if (showArchived) setArchived(nextPool);
    else setFiches(nextPool);
    void saveFicheOrder(nextPool.map((row) => row.id)).catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Ordre non enregistré");
    });
  }

  const sheets = (
    <>
      {naming ? (
        <Modal onClose={() => { if (!creating) setNaming(false); }} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void createNamed();
              }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Nouvelle fiche</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight">{space === "interne" ? "CRM interne" : "CRM clients"}</h2>
              <p className="mt-3 text-sm leading-relaxed opacity-80">
                {space === "interne"
                  ? "Le dossier est créé dans CRM interne. Société, projet, ou ce que tu veux. Les notes, le coffre et le suivi y vivent."
                  : "Le dossier est créé dans CRM clients, au nom de la fiche. Les notes, les pages et les kanbans y vivent."}
              </p>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium opacity-70">Nom de la fiche</span>
                <input
                  autoFocus
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  className="crm-field w-full rounded-xl border px-3 py-2"
                  placeholder={space === "interne" ? "Nom du projet ou du dossier" : "Nom du client ou du dossier"}
                />
              </label>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold" onClick={close} disabled={creating}>
                  Annuler
                </button>
                <button type="submit" className="rounded-full crm-fill px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={creating || draftName.trim() === ""}>
                  {creating ? "Création…" : "Créer"}
                </button>
              </div>
            </form>
          )}
        </Modal>
      ) : null}
      {pending ? (
        <Modal onClose={() => { if (!dropping) setPending(null); }} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Archiver la fiche</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight">{pending.name}</h2>
              <p className="mt-3 text-sm leading-relaxed opacity-80">
                {pending.scope === "interne"
                  ? "La fiche sort de la liste active. Son dossier dans CRM interne reste en place. Depuis les archives, tu pourras la réactiver, ou la supprimer : le dossier suivra alors dans la corbeille du Coffre."
                  : "La fiche sort de la liste active. Son dossier dans CRM clients reste en place, avec les notes et les pages. Depuis les archives, tu pourras la réactiver, ou la supprimer : le dossier suivra alors dans la corbeille du Coffre."}
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold" onClick={close} disabled={dropping}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={dropping}
                  onClick={() => {
                    const id = pending.id;
                    setDropping(true);
                    void drop(id)
                      .then(() => close())
                      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Suppression impossible"))
                      .finally(() => setDropping(false));
                  }}
                >
                  {dropping ? "Archivage…" : "Archiver la fiche"}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
      {erasing ? (
        <Modal onClose={() => { if (!dropping) setErasing(null); }} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Supprimer la fiche</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight">{erasing.name}</h2>
              <p className="mt-3 text-sm leading-relaxed opacity-80">
                La fiche quitte le CRM : suivi, contacts, affaires et lien Factur-X sont effacés, ainsi que les tâches de la semaine qui lui sont liées. Son dossier part dans la corbeille du Coffre, avec les notes, les pages et les kanbans. Vider la corbeille retire aussi ces fichiers du disque.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold" onClick={close} disabled={dropping}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={dropping}
                  onClick={() => {
                    const id = erasing.id;
                    setDropping(true);
                    void erase(id)
                      .then(() => close())
                      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Suppression impossible"))
                      .finally(() => setDropping(false));
                  }}
                >
                  {dropping ? "Suppression…" : "Supprimer la fiche"}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );

  if (phone) {
    return (
      <AccessScope>
      <>
        <Phone
          ready={ready}
          error={error}
          fiches={clientFiches}
          visible={phoneVisible}
          showArchived={showArchived}
          onToggleArchived={() => { setShowArchived((next) => !next); setFiche(null); setBureau(false); }}
          kind={kind}
          onKind={setKind}
          marks={marks}
          due={due}
          fiche={fiche?.scope === "interne" ? null : fiche}
          saved={saved && fiche?.scope !== "interne"}
          editing={tab === "fiche"}
          onEdit={() => setTab("fiche")}
          onSaved={() => setTab("apercu")}
          onOpen={(row) => { setSpace("client"); setFiche(row); setTab("apercu"); setBureau(false); setMonth(false); setError(""); }}
          onClose={() => setFiche(null)}
          onChange={remember}
          onStatus={paint}
          onNew={() => { setSpace("client"); startNew(); }}
          onArchive={setPending}
          onErase={setErasing}
          onRestore={(id) => { void restore(id).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Réactivation impossible")); }}
          onRetired={retire}
        />
        {sheets}
        <CoffreUnlockHost />
      </>
      </AccessScope>
    );
  }

  return (
    <AccessScope>
    <div className="crm-app flex h-screen w-screen flex-col">
      <header className="crm-surface grid grid-cols-[minmax(0,1fr)_minmax(16rem,28rem)_minmax(0,1fr)] items-center gap-3 border-b border-slate-200 px-5 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="crm-fill grid h-11 w-11 shrink-0 place-items-center rounded-full">
            <LogoIcon />
          </span>
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold leading-tight tracking-tight">{space === "interne" ? "Interne" : "Aurélia CRM"}</p>
            <p className="truncate text-sm text-slate-500">{space === "interne" ? "Fiches d’équipe" : "Gestion de la Relation Client"}</p>
          </div>
        </div>
        <div className="relative">
          <input className="crm-seek h-10 w-full rounded-full border px-4 text-sm" value={query} placeholder="Fiche, tâche, note, devis…" onChange={(event) => { setQuery(event.target.value); setSeek(true); }} onFocus={() => setSeek(true)} onBlur={() => setSeek(false)} />
          {seek && query.trim().length >= 2 ? <Finder query={query} fiches={space === "interne" ? [...interneFiches, ...interneArchived] : [...clientFiches, ...clientArchived]} onPick={openHit} /> : null}
        </div>
        <div className="flex justify-end">
          <GuideButton />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className={slim ? "crm-rail crm-surface relative z-[3] flex w-16 shrink-0 flex-col border-r border-slate-200 px-1.5 py-4 transition-[width] duration-200" : "crm-rail crm-surface relative z-[3] flex w-64 shrink-0 flex-col border-r border-slate-200 px-3 py-4 transition-[width] duration-200"}>
          <button
            type="button"
            className={slim ? "mx-auto grid h-9 w-9 place-items-center rounded-full crm-fill text-base font-semibold text-white" : "flex items-center justify-center gap-2 rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white"}
            title="Nouvelle fiche"
            aria-label="Nouvelle fiche"
            onClick={startNew}
          >
            <span className="text-base leading-none">+</span>
            {slim ? null : "Fiche"}
          </button>
          <div className={slim ? "mt-2 flex flex-col items-center gap-1" : "crm-pill mt-3 grid grid-cols-2 rounded-full p-0.5 text-xs font-semibold"}>
            <button type="button" className={space === "client" ? (slim ? "crm-fill grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold text-white" : "crm-fill rounded-full px-2 py-1 text-white") : (slim ? "grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold" : "rounded-full px-2 py-1")} title="Clients" aria-label="Clients" onClick={() => enterSpace("client")}>{slim ? "C" : "Clients"}</button>
            <button type="button" className={space === "interne" ? (slim ? "crm-fill grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold text-white" : "crm-fill rounded-full px-2 py-1 text-white") : (slim ? "grid h-8 w-8 place-items-center rounded-full text-[11px] font-semibold" : "rounded-full px-2 py-1")} title="Interne" aria-label="Interne" onClick={() => enterSpace("interne")}>{slim ? "I" : "Interne"}</button>
          </div>
          {slim ? (
            <>
              <button type="button" title="Semaine" aria-label="Semaine" className={navClass(!fiche && !bureau && !month && !showArchived, true)} onClick={() => { setBureau(false); setMonth(false); setFiche(null); setShowArchived(false); setError(""); }}>
                <WeekIcon />
              </button>
              <button type="button" title="Mois" aria-label="Mois" className={navClass(month && !fiche && !showArchived, true)} onClick={() => { setBureau(false); setMonth(true); setFiche(null); setShowArchived(false); setError(""); }}>
                <MonthIcon />
              </button>
            </>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" className={pairClass(!fiche && !bureau && !month && !showArchived)} onClick={() => { setBureau(false); setMonth(false); setFiche(null); setShowArchived(false); setError(""); }}>Semaine</button>
              <button type="button" className={pairClass(month && !fiche && !showArchived)} onClick={() => { setBureau(false); setMonth(true); setFiche(null); setShowArchived(false); setError(""); }}>Mois</button>
            </div>
          )}
          <button type="button" title="Bureau" aria-label="Bureau" className={navClass(bureau && !fiche, slim)} onClick={() => { setBureau(true); setMonth(false); setFiche(null); setShowArchived(false); setError(""); }}>
            {slim ? <DeskIcon /> : "Bureau"}
          </button>
          {space === "interne" || slim ? null : (
            <div className="crm-pill mt-4 flex rounded-full p-0.5 text-[10px] font-semibold">
              {([["", "Tous"], ["prospect", "Prospect"], ["client", "Client"], ["former", "Ancien"]] as const).map(([value, label]) => (
                <button key={value || "all"} type="button" className={kind === value ? "crm-fill min-w-0 flex-1 rounded-full px-1 py-1 text-white" : "min-w-0 flex-1 rounded-full px-1 py-1"} onClick={() => setKind(value)}>{label}</button>
              ))}
            </div>
          )}
          {slim ? null : (
            <div className="mt-4 flex items-center justify-between px-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{showArchived ? "Archivées" : space === "interne" ? "Interne" : "Fiches"}</p>
              <button type="button" className="text-[11px] font-semibold text-slate-500" onClick={() => { setShowArchived((next) => !next); setFiche(null); setBureau(false); }}>{showArchived ? "Actives" : "Archives"}</button>
            </div>
          )}
          <ReceivedStrip />
          <ul ref={listRef} className="mt-2 min-h-0 flex-1 space-y-1 overflow-auto">
            {ready && visible.length === 0 ? <li className="px-2 py-3 text-sm text-slate-400">{slim ? "—" : showArchived ? "Aucune fiche archivée." : "Aucune fiche enregistrée."}</li> : null}
            {visible.map((row, index) => {
              const on = row.id === fiche?.id;
              const count = due[row.id] || 0;
              const todayLabel = count > 1 ? `${count} tâches aujourd’hui` : count === 1 ? "1 tâche aujourd’hui" : "";
              return (
              <li key={row.id} data-fiche={row.id} onPointerDown={(event) => grabFiche(row.id, event)} className={[on ? (slim ? "crm-rise crm-soft flex cursor-grab items-center justify-center rounded-xl py-1" : "crm-rise crm-soft flex cursor-grab items-center rounded-xl pr-1") : (slim ? "crm-rise flex cursor-grab items-center justify-center rounded-xl py-1 hover:bg-black/5" : "crm-rise flex cursor-grab items-center rounded-xl pr-1 hover:bg-black/5"), lift === row.id ? "opacity-40" : "", slot === index ? "crm-fiche-before" : ""].filter(Boolean).join(" ")} style={{ animationDelay: `${index * 0.04}s` }}>
                <button
                  type="button"
                  title={todayLabel ? `${row.name} — ${todayLabel}` : row.name}
                  aria-label={todayLabel ? `${row.name}, ${todayLabel}` : row.name}
                  className={slim ? "grid place-items-center bg-transparent p-1" : (on ? "flex min-w-0 flex-1 items-center gap-2.5 bg-transparent px-2.5 py-2 text-left text-sm font-semibold" : "flex min-w-0 flex-1 items-center gap-2.5 bg-transparent px-2.5 py-2 text-left text-sm font-medium text-slate-700")}
                  onClick={() => {
                    setFiche(row);
                    setTab("apercu");
                    setBureau(false);
                    setEditName(false);
                    setError("");
                  }}
                >
                  <span className="relative shrink-0">
                    <FicheMark path={row.logo_path} name={row.name} bare={row.scope === "interne"} tone={row.scope === "interne" ? undefined : (marks[row.id] || "prospect")} className="h-8 w-8 text-xs" />
                    {count > 0 ? <span className="crm-day-dot">{count > 9 ? "9+" : count}</span> : null}
                  </span>
                  {slim ? null : (
                    <span className="min-w-0">
                      <span className="block truncate">{row.name}</span>
                      <FicheShareHint fiche={row} />
                    </span>
                  )}
                </button>
                {slim || !row.mine ? null : <>
                <button
                  type="button"
                  data-no-drag
                  aria-label={`Modifier ${row.name}`}
                  title={`Modifier ${row.name}`}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-transparent text-current opacity-70 hover:opacity-100"
                  onClick={() => {
                    setFiche(row);
                    setBureau(false);
                    setError("");
                    if (row.scope === "interne") setEditName(true);
                    else setTab("fiche");
                  }}
                >
                  <PencilIcon />
                </button>
                {showArchived ? (
                  <button
                    type="button"
                    data-no-drag
                    aria-label={`Supprimer ${row.name}`}
                    title={`Supprimer ${row.name}`}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-transparent text-rose-600 opacity-80 hover:opacity-100"
                    onClick={() => setErasing(row)}
                  >
                    <TrashIcon />
                  </button>
                ) : null}
                <button
                  type="button"
                  data-no-drag
                  aria-label={showArchived ? `Réactiver ${row.name}` : `Archiver ${row.name}`}
                  title={showArchived ? `Réactiver ${row.name}` : `Archiver ${row.name}`}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-transparent text-current opacity-70 hover:opacity-100"
                  onClick={() => {
                    if (showArchived) {
                      void restore(row.id).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Réactivation impossible"));
                      return;
                    }
                    setPending(row);
                  }}
                >
                  {showArchived ? <span className="text-xs font-semibold">OK</span> : <TrashIcon />}
                </button>
                </>}
              </li>
              );
            })}
            {slot === visible.length ? <li className="crm-fiche-slot" aria-hidden /> : null}
          </ul>
          <footer className="mt-3 shrink-0">
            <div className={slim ? "flex flex-col items-center gap-1" : "flex items-center justify-center gap-1"}>
              <ThemeMenu />
              <button
                type="button"
                className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-black/5"
                aria-label={slim ? "Ouvrir la barre" : "Réduire la barre"}
                title={slim ? "Ouvrir la barre" : "Réduire la barre"}
                aria-expanded={!slim}
                onClick={() => setSlim((next) => !next)}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d={slim ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"} />
                </svg>
              </button>
            </div>
            {slim ? null : (
              <div className="mt-3 border-t border-slate-200 px-2 pt-3 text-center text-[10px] leading-relaxed text-slate-500">
                © {new Date().getFullYear()} <a className="font-semibold crm-ink hover:underline" href="https://aureliasuite.fr" target="_blank" rel="noopener noreferrer">Aurélia Suite</a>. Tous droits réservés
                <span className="mx-1 opacity-50" aria-hidden>•</span>
                Conforme RGPD
                <span className="mx-1 opacity-50" aria-hidden>•</span>
                Studio Seth — Pacy-sur-Eure (27)
              </div>
            )}
          </footer>
        </aside>
        <main className="relative min-w-0 flex-1 overflow-auto">
          <FadeSwap id={viewKey || "boot"} className="min-h-full">{fiche && saved && fiche.scope === "interne" ? (
            <SuiviScope ficheId={fiche.id} onStatus={() => undefined}>
              {error ? <p className="px-6 pt-4 text-center text-sm text-rose-700">{error}</p> : null}
              <InterneDesk fiche={fiche} onShare={remember} naming={editName} onNaming={setEditName} />
            </SuiviScope>
          ) : fiche && saved ? (
            <SuiviScope ficheId={fiche.id} onStatus={(status) => paint(fiche.id, status)}>
              <FicheHead fiche={fiche} tone={marks[fiche.id] || "prospect"} onShare={remember} />
              {error ? <p className="px-6 pt-4 text-center text-sm text-rose-700">{error}</p> : null}
              <Appear>
                <div className={tab === "fiche" ? undefined : "hidden"} aria-hidden={tab !== "fiche"}>
                  {tab === "fiche" ? <FicheDesk fiche={fiche} others={[...clientFiches, ...clientArchived]} onChange={remember} onSaved={() => setTab("apercu")} onOpen={(row) => { setFiche(row); setTab("apercu"); }} onRetired={retire} /> : null}
                </div>
                <div className={tab === "apercu" ? undefined : "hidden"} aria-hidden={tab !== "apercu"}>
                  <Cockpit fiche={fiche} onChange={remember} />
                </div>
              </Appear>
            </SuiviScope>
          ) : (
            <>
          {error ? <p className="px-6 pt-4 text-center text-sm text-rose-700">{error}</p> : null}
          {!ready ? null : bureau && !fiche ? (
            <BureauView onOpen={(id) => {
              const row = fiches.find((item) => item.id === id);
              if (!row) return;
              setSpace(row.scope === "interne" ? "interne" : "client");
              setFiche(row);
              setTab("apercu");
              setBureau(false);
              setShowArchived(false);
            }} />
          ) : !fiche ? (
            showArchived ? <p className="px-6 py-8 text-sm text-slate-500">Choisissez une fiche archivée.</p>
            : space === "client" && clientFiches.length === 0 ? <Welcome onChoose={startNew} />
            : month ? <MonthBoard fiches={fiches} onOpenFiche={openFromBoard} />
            : <WeekBoard fiches={fiches} focusId={focusTask} onFocusDone={dropFocus} onOpenFiche={openFromBoard} />
          ) : (
            <FicheDesk fiche={fiche} others={[...clientFiches, ...clientArchived]} onChange={remember} onSaved={() => setTab("apercu")} onOpen={(row) => { setFiche(row); setTab("apercu"); setBureau(false); setMonth(false); }} onRetired={retire} />
          )}
            </>
          )}</FadeSwap>
        </main>
      </div>
      {sheets}
      <CoffreUnlockHost />
    </div>
    </AccessScope>
  );
}

function ficheSlot(list: HTMLUListElement | null, y: number): number {
  const rows = list ? [...list.querySelectorAll<HTMLElement>("[data-fiche]")] : [];
  const hit = rows.findIndex((row) => y < row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2);
  return hit < 0 ? rows.length : hit;
}

function pairClass(on: boolean): string {
  return on ? "crm-soft min-w-0 rounded-full border border-transparent px-3 py-2 text-sm font-semibold" : "min-w-0 rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold";
}

function navClass(on: boolean, slim: boolean): string {
  if (slim) return on ? "crm-soft mx-auto mt-2 grid h-9 w-9 place-items-center rounded-xl border border-transparent" : "mx-auto mt-2 grid h-9 w-9 place-items-center rounded-xl border border-slate-200";
  return on ? "crm-soft mt-3 w-full rounded-full border border-transparent px-3 py-2 text-sm font-semibold" : "mt-3 w-full rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold";
}

function WeekIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path strokeLinecap="round" d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
    </svg>
  );
}

function MonthIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path strokeLinecap="round" d="M3.5 9h17M8 4.5v4.5M12 4.5v4.5M16 4.5v4.5M8 13h.01M12 13h.01M16 13h.01M8 16.5h.01M12 16.5h.01" />
    </svg>
  );
}

function DeskIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="3.5" y="4" width="7" height="7" rx="1.4" />
      <rect x="13.5" y="4" width="7" height="7" rx="1.4" />
      <rect x="3.5" y="13" width="17" height="7" rx="1.4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10.2-10.2a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 7l.7 12.2A1.5 1.5 0 0 0 8.7 20.5h6.6a1.5 1.5 0 0 0 1.5-1.3L17.5 7" />
      <path strokeLinecap="round" d="M10 11v6M14 11v6" />
    </svg>
  );
}

function LogoIcon() {
  return (
    <svg viewBox="1.2 3.4 20.4 17" className="h-[30px] w-[30px]" fill="none" stroke="currentColor" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.6" />
      <path d="M7.2 4.6v14.8" />
      <path d="M2.1 8.4h2.2" />
      <path d="M2.1 15.6h2.2" />
      <circle cx="14.4" cy="10" r="2.15" />
      <path d="M11.4 14.9a3.7 2.45 0 0 1 6 0" />
    </svg>
  );
}
